import { type CheckoutSession, SessionError } from "@/types";
import { emptySession } from "@/lib/checkout";
import {
  emitUndefinedBehaviorTracking,
  type UndefinedBehaviorEventData,
} from "@/shared/tracking/analytics";
import { logError, logInfo, logWarn } from "@/services/logging/logger";

/**
 * HTTP error types for recovery decisions
 */
export type HttpErrorType =
  | "conflict" // 409 - state mismatch, recoverable
  | "not_found" // 404 - resource doesn't exist
  | "client_error" // other 4xx - potentially recoverable
  | "server_error" // 5xx - not recoverable
  | "network_error" // no response - potentially recoverable
  | "unknown"; // other errors

/**
 * Typed error structure from API responses
 */
export interface ApiError {
  response?: {
    status?: number;
    statusText?: string;
    data?: {
      message?: string;
      error_code?: string;
      error_message?: string;
    };
  };
  config?: {
    url?: string;
    method?: string;
    data?: unknown;
  };
  message?: string;
  request?: unknown;
}

/**
 * Recovery result from error handling
 */
export interface RecoveryResult {
  /** The recovered session (either from refetch or error session) */
  session: CheckoutSession;
  /** Whether the recovery was successful (i.e., got valid session from refetch) */
  recovered: boolean;
  /** The action that was taken */
  action: "refetch_session" | "show_error_screen" | "page_refresh";
  /** Original error for logging purposes */
  originalError: ApiError;
}

/**
 * Options for HTTP error recovery
 */
export interface RecoveryOptions {
  /** Function to refetch the current session from API */
  refetchSession: () => Promise<CheckoutSession>;
  /** The current session (for tracking context) */
  currentSession?: CheckoutSession;
  /** The endpoint that failed */
  endpoint: string;
  /** HTTP method used */
  httpMethod: string;
  /** Additional context for tracking */
  requestContext?: Record<string, unknown>;
}

/**
 * Classify the HTTP error type based on status code
 */
export function classifyHttpError(error: ApiError): HttpErrorType {
  const status = error.response?.status;

  if (!status) {
    // No response - could be network error or request setup error
    return error.request ? "network_error" : "unknown";
  }

  if (status === 409) return "conflict";
  if (status === 404) return "not_found";
  if (status >= 400 && status < 500) return "client_error";
  if (status >= 500) return "server_error";

  return "unknown";
}

/**
 * Check if the error is a 4xx client error (potentially recoverable via refetch)
 */
export function isRecoverableClientError(error: ApiError): boolean {
  const errorType = classifyHttpError(error);
  // 409 Conflict and other client errors (except 404) might be recoverable
  // by refetching the session to get the current state
  return errorType === "conflict" || errorType === "client_error";
}

/**
 * Check if the error is a server error (not recoverable)
 */
export function isServerError(error: ApiError): boolean {
  return classifyHttpError(error) === "server_error";
}

/**
 * Create an error session for showing the error screen
 */
function createErrorSession(
  error: ApiError,
  errorType: HttpErrorType,
): CheckoutSession {
  const sessionErrorType =
    errorType === "not_found" ? SessionError.NOT_FOUND : SessionError.UNKNOWN;

  const errorMessage =
    error.response?.data?.error_message ||
    error.response?.data?.message ||
    error.message ||
    "";

  return {
    ...emptySession,
    error: {
      type: sessionErrorType,
      message: errorMessage,
      code: error.response?.data?.error_code,
    },
  };
}

/**
 * Track the undefined behavior event
 */
function trackUndefinedBehavior(
  error: ApiError,
  recoveryAction: UndefinedBehaviorEventData["recovery_action"],
  recoverySuccessful: boolean,
  options: RecoveryOptions,
): void {
  const eventData: UndefinedBehaviorEventData = {
    http_status: error.response?.status ?? 0,
    http_method: options.httpMethod.toUpperCase(),
    endpoint: options.endpoint,
    error_code: error.response?.data?.error_code,
    error_message:
      error.response?.data?.error_message ||
      error.response?.data?.message ||
      error.message,
    frontend_session_status: options.currentSession?.status ?? "unknown",
    recovery_action: recoveryAction,
    recovery_successful: recoverySuccessful,
    request_context: options.requestContext,
  };

  emitUndefinedBehaviorTracking(eventData, options.currentSession);
}

/**
 * Handle HTTP errors gracefully with smart recovery
 *
 * Recovery strategy:
 * - 409 Conflict: Refetch session to sync state, navigate based on actual status
 * - 404 Not Found: Show error screen (session doesn't exist)
 * - Other 4xx: Try to refetch session, fallback to error screen
 * - 5xx: Show error screen (server broken)
 * - Network error: Try to refetch, fallback to error screen
 *
 * @param error - The error from the API call
 * @param options - Recovery options including refetch function
 * @returns RecoveryResult with the recovered session and action taken
 */
export async function handleHttpError(
  error: ApiError,
  options: RecoveryOptions,
): Promise<RecoveryResult> {
  const errorType = classifyHttpError(error);
  const status = error.response?.status ?? 0;

  logWarn("http-error-recovery", `Handling HTTP error: ${status} ${errorType}`, {
    status,
    errorType,
    endpoint: options.endpoint,
    method: options.httpMethod,
    errorCode: error.response?.data?.error_code,
    errorMessage: error.response?.data?.error_message || error.message,
    currentSessionStatus: options.currentSession?.status,
  });

  // 404 - Session not found, show error screen
  if (errorType === "not_found") {
    logInfo("http-error-recovery", "Session not found, showing error screen");
    trackUndefinedBehavior(error, "show_error_screen", false, options);
    return {
      session: createErrorSession(error, errorType),
      recovered: false,
      action: "show_error_screen",
      originalError: error,
    };
  }

  // 5xx - Server error, show error screen
  if (errorType === "server_error") {
    logError(
      "http-error-recovery",
      "Server error, showing error screen",
      new Error(`Server error: ${status}`),
      { status, endpoint: options.endpoint },
    );
    trackUndefinedBehavior(error, "show_error_screen", false, options);
    return {
      session: createErrorSession(error, errorType),
      recovered: false,
      action: "show_error_screen",
      originalError: error,
    };
  }

  // 409 or other 4xx - Try to refetch session to sync state
  if (isRecoverableClientError(error) || errorType === "network_error") {
    logInfo(
      "http-error-recovery",
      `Attempting recovery for ${errorType}, refetching session`,
      {
        status,
        errorType,
        endpoint: options.endpoint,
      },
    );

    try {
      const freshSession = await options.refetchSession();

      // Validate that we got a valid session
      if (freshSession && freshSession.id && freshSession.status) {
        logInfo(
          "http-error-recovery",
          "Successfully recovered session via refetch",
          {
            sessionId: freshSession.id,
            status: freshSession.status,
            previousStatus: options.currentSession?.status,
          },
        );
        trackUndefinedBehavior(error, "refetch_session", true, options);
        return {
          session: freshSession,
          recovered: true,
          action: "refetch_session",
          originalError: error,
        };
      }

      // Session refetch succeeded but data is invalid
      logWarn(
        "http-error-recovery",
        "Session refetch returned invalid data, showing error screen",
        { freshSession },
      );
      trackUndefinedBehavior(error, "show_error_screen", false, options);
      return {
        session: createErrorSession(error, errorType),
        recovered: false,
        action: "show_error_screen",
        originalError: error,
      };
    } catch (refetchError) {
      // Refetch failed, fall back to error screen
      logError(
        "http-error-recovery",
        "Session refetch failed during recovery",
        refetchError instanceof Error
          ? refetchError
          : new Error(String(refetchError)),
        {
          originalStatus: status,
          originalEndpoint: options.endpoint,
        },
      );
      trackUndefinedBehavior(error, "show_error_screen", false, options);
      return {
        session: createErrorSession(error, errorType),
        recovered: false,
        action: "show_error_screen",
        originalError: error,
      };
    }
  }

  // Unknown error type, show error screen
  logWarn("http-error-recovery", "Unknown error type, showing error screen", {
    status,
    errorType,
  });
  trackUndefinedBehavior(error, "show_error_screen", false, options);
  return {
    session: createErrorSession(error, errorType),
    recovered: false,
    action: "show_error_screen",
    originalError: error,
  };
}

/**
 * Simple check if an error response indicates a state conflict (409)
 * Useful for quick checks without full recovery handling
 */
export function isConflictError(error: unknown): boolean {
  const apiError = error as ApiError;
  return apiError?.response?.status === 409;
}

/**
 * Extract HTTP status from an error
 */
export function getErrorStatus(error: unknown): number {
  return (error as ApiError)?.response?.status ?? 0;
}

/**
 * Format error for logging
 */
export function formatErrorForLogging(error: ApiError): Record<string, unknown> {
  return {
    status: error.response?.status,
    statusText: error.response?.statusText,
    errorCode: error.response?.data?.error_code,
    errorMessage:
      error.response?.data?.error_message ||
      error.response?.data?.message ||
      error.message,
    endpoint: error.config?.url,
    method: error.config?.method,
  };
}
